import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/authSlice";
import { ENV } from "../../config/env";
import { api } from "../../lib/api";
import toast from "react-hot-toast";

const getToken = () => localStorage.getItem("admin_token");

const ALLOWED_TYPES = [
  "image/jpeg", "image/jpg", "image/png", "image/gif",
  "image/webp", "image/bmp", "image/avif", "image/heic", "image/heif",
];

const TIME_BRACKETS = [
  {
    value: "day",
    label: "Day",
    icon: "☀️",
    bracket: "06:00 AM – 06:00 PM",
    description: "Shown for flights departing between 6:00 AM and 6:00 PM",
    selected: "border-amber-500 bg-amber-50 ring-2 ring-amber-400",
    idle: "border-gray-200 hover:border-amber-300",
  },
  {
    value: "night",
    label: "Night",
    icon: "🌙",
    bracket: "06:00 PM – 06:00 AM",
    description: "Shown for flights departing between 6:00 PM and 6:00 AM",
    selected: "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-400",
    idle: "border-gray-200 hover:border-indigo-300",
  },
];

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export default function CityImageUpload() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isSuperadmin = Number(user?.role_id) === 1;

  // City search
  const [cityQuery, setCityQuery] = useState("");
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const cityInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Image type
  const [imageType, setImageType] = useState("cityImage");

  // Time of day
  const [timeOfDay, setTimeOfDay] = useState("");

  // Multiple files
  const [files, setFiles] = useState([]); // [{ id, file, preview }]
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Submit
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isSuperadmin) navigate("/admin/city-images", { replace: true });
  }, [isSuperadmin, navigate]);

  // Debounced city search — fires from first character
  useEffect(() => {
    const q = cityQuery.trim();
    if (!q) { setCitySuggestions([]); return; }
    const t = setTimeout(async () => {
      setCityLoading(true);
      try {
        const { data } = await api.get(
          `/admin/city-images/cities/search?q=${encodeURIComponent(q)}`
        );
        setCitySuggestions(data.data || []);
      } catch {
        setCitySuggestions([]);
      } finally {
        setCityLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [cityQuery]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        !cityInputRef.current?.contains(e.target) &&
        !suggestionsRef.current?.contains(e.target)
      ) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Revoke object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => files.forEach((f) => URL.revokeObjectURL(f.preview));
  }, []);

  const selectCity = (city) => {
    setSelectedCity(city);
    setCityQuery(`${city.city_name} (${city.city_code})`);
    setShowSuggestions(false);
    setCitySuggestions([]);
  };

  const addFiles = (incoming) => {
    const valid = Array.from(incoming).filter((f) => ALLOWED_TYPES.includes(f.type));
    if (valid.length !== incoming.length) {
      toast.error("Some files were skipped — only image files are allowed.");
    }
    setFiles((prev) => {
      // Deduplicate by name+size
      const existing = new Set(prev.map((f) => `${f.file.name}|${f.file.size}`));
      const fresh = valid
        .filter((f) => !existing.has(`${f.name}|${f.size}`))
        .map((f) => ({ id: `${f.name}-${f.size}-${Math.random()}`, file: f, preview: URL.createObjectURL(f) }));
      return [...prev, ...fresh];
    });
  };

  const removeFile = (id) => {
    setFiles((prev) => {
      const removed = prev.find((f) => f.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCity) { toast.error("Please select a city."); return; }
    if (!timeOfDay) { toast.error("Please select Day or Night."); return; }
    if (!files.length) { toast.error("Please add at least one image."); return; }

    setSubmitting(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f.file));
      formData.append("city_name", selectedCity.city_name);
      formData.append("city_code", selectedCity.city_code);
      formData.append("iata_codes", JSON.stringify(selectedCity.iata_codes));
      formData.append("country_code", selectedCity.country_code || "");
      formData.append("country_name", selectedCity.country_name || "");
      formData.append("time_of_day", timeOfDay);
      formData.append("image_type", imageType);

      const res = await fetch(`${ENV.API_BASE_URL}/admin/city-images`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      const json = await res.json();

      if (!res.ok || !json.success) throw new Error(json.message || "Upload failed.");

      files.forEach((f) => URL.revokeObjectURL(f.preview));
      setFiles([]);
      toast.success("Images submitted for review.");
      navigate("/admin/city-images");
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate("/admin/city-images")}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Upload City Images</h1>
      </div>

      {/* <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-6 text-sm text-blue-700">
        Uploaded images will be sent to the Super Admin for review before going live.
        You can upload multiple images at once for the same city and time bracket.
      </div> */}

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* ── City search ── */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <div ref={cityInputRef}>
            <input
              type="text"
              value={cityQuery}
              onChange={(e) => {
                setCityQuery(e.target.value);
                setSelectedCity(null);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Type city name (e.g. Mumbai, London, Dubai)"
              autoComplete="off"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {showSuggestions && (cityLoading || citySuggestions.length > 0) && (
            <ul
              ref={suggestionsRef}
              className="absolute z-10 w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-1 max-h-72 overflow-y-auto"
            >
              {cityLoading && (
                <li className="px-4 py-3 text-sm text-gray-400">Searching…</li>
              )}
              {!cityLoading && citySuggestions.length === 0 && (
                <li className="px-4 py-3 text-sm text-gray-400">No cities found.</li>
              )}
              {citySuggestions.map((city) => (
                <li key={`${city.city_name}|${city.country_code}`} className="border-b last:border-0">
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectCity(city)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <span className="font-medium text-gray-800 text-sm">{city.city_name}</span>
                        <span className="ml-1.5 text-gray-400 text-xs font-mono">{city.city_code}</span>
                      </div>
                      <span className="text-xs text-gray-400 shrink-0">{city.country_name}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {city.iata_codes.map((code) => (
                        <span key={code} className="bg-gray-100 text-gray-600 text-xs font-mono px-1.5 py-0.5 rounded">
                          {code}
                        </span>
                      ))}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Selected city panel ── */}
        {selectedCity && (
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">Selected City</p>
              <span className="text-xs text-green-600">{selectedCity.country_name}</span>
            </div>
            <p className="text-sm font-semibold text-gray-800 mb-2">
              {selectedCity.city_name}
              <span className="ml-2 font-mono text-gray-500 font-normal text-xs">{selectedCity.city_code}</span>
            </p>
            <p className="text-xs text-gray-500 mb-2">All airport codes linked to this image:</p>
            <div className="flex flex-wrap gap-2">
              {selectedCity.iata_codes.map((code) => (
                <span key={code} className="bg-white border border-green-300 text-gray-700 text-sm font-mono px-3 py-1 rounded-lg">
                  {code}
                </span>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {selectedCity.iata_codes.length === 1
                ? "This city has 1 airport."
                : `This city has ${selectedCity.iata_codes.length} airports — the same images will serve all of them.`}
            </p>
          </div>
        )}

        {/* ── Time bracket ── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Time Bracket <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            {TIME_BRACKETS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTimeOfDay(t.value)}
                className={`border-2 rounded-xl px-4 py-4 text-left transition ${timeOfDay === t.value ? t.selected : t.idle}`}
              >
                <div className="text-2xl mb-1">{t.icon}</div>
                <div className="font-semibold text-gray-800 text-sm">{t.label}</div>
                <div className="text-xs font-mono text-gray-500 mt-0.5">{t.bracket}</div>
                <div className="text-xs text-gray-400 mt-1 leading-snug">{t.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Image Type ── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image Type <span className="text-red-500">*</span>
          </label>
          <select
            value={imageType}
            onChange={(e) => setImageType(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="cityImage">City Image</option>
            <option value="logo">Logo</option>
            <option value="airline_logo">Airline Logo</option>
          </select>
        </div>

        {/* ── Drop zone ── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image Files <span className="text-red-500">*</span>
            <span className="ml-2 text-xs text-gray-400 font-normal">You can select multiple images</span>
          </label>

          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl px-6 py-8 text-center cursor-pointer transition-colors ${
              isDragging ? "border-blue-400 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFileInput}
            />
            <div className="text-4xl mb-2">📁</div>
            <p className="font-medium text-gray-700 text-sm">Drop images here or click to browse</p>
            <p className="text-xs text-gray-400 mt-1">JPG · PNG · GIF · WebP · BMP · AVIF · Max 50 MB per file</p>
            <p className="text-xs text-gray-400 mt-0.5">Files stored with original name and size — no compression applied.</p>
          </div>
        </div>

        {/* ── Selected files list ── */}
        {files.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">
                {files.length} file{files.length > 1 ? "s" : ""} selected
              </p>
              <button
                type="button"
                onClick={() => { files.forEach((f) => URL.revokeObjectURL(f.preview)); setFiles([]); }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Remove all
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {files.map((item) => (
                <div key={item.id} className="relative group rounded-xl overflow-hidden border border-gray-200">
                  <img
                    src={item.preview}
                    alt={item.file.name}
                    className="w-full h-32 object-cover"
                  />
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 px-2">
                    <p className="text-white text-xs font-medium text-center truncate w-full px-1" title={item.file.name}>
                      {item.file.name}
                    </p>
                    <p className="text-white/70 text-xs">{formatBytes(item.file.size)}</p>
                  </div>
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => removeFile(item.id)}
                    className="absolute top-1.5 right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow transition"
                    title="Remove"
                  >
                    ✕
                  </button>
                  {/* Filename below */}
                  <div className="px-2 py-1.5 bg-white">
                    <p className="text-xs text-gray-600 truncate" title={item.file.name}>{item.file.name}</p>
                    <p className="text-xs text-gray-400">{formatBytes(item.file.size)}</p>
                  </div>
                </div>
              ))}

              {/* Add more button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="h-[calc(8rem+52px)] rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-gray-50 flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-blue-500 transition"
              >
                <span className="text-2xl">+</span>
                <span className="text-xs">Add more</span>
              </button>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium text-sm hover:bg-blue-700 transition disabled:opacity-60"
        >
          {submitting
            ? `Uploading ${files.length} image${files.length > 1 ? "s" : ""}…`
            : `Submit ${files.length > 0 ? `${files.length} Image${files.length > 1 ? "s" : ""}` : "Images"} for Review`}
        </button>
      </form>
    </div>
  );
}
